// src/components/Admin/ColegiadoTable.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  MenuItem,
  Paper,
} from "@mui/material";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { obtenerBICdesdeBanco } from "../common/Bics";
import { validarIBAN } from "../Utiles/Verificar/iban";
import { validarDNI } from "../Utiles/Verificar/dni";
import { validarEmail } from "../Utiles/Verificar/email";
import SelectEstado from "../common/Estados";
import EuroIcon from "@mui/icons-material/Euro";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import GetAppIcon from "@mui/icons-material/GetApp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import DoDisturbOffIcon from "@mui/icons-material/DoDisturbOff";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ColegiadoEditDialog from "../Admin/ColegiadoEditDialog";
import BulkEditDialog from "../Admin/BulkEditDialog";
import { Alert, Checkbox } from "@mui/material";
import EstadisticasIne from "../Admin/EstadisticasIne";
import NuevoColegiadoDialog from "../Admin/NuevoColegiadoDialog";

// ---------------- Utils ----------------
// Conversión fechas
const dmyToIso = (dmy) => {
  if (!dmy) return "";
  const m = String(dmy).trim().match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (!m) return "";
  const dd = m[1].padStart(2, "0");
  const mm = m[2].padStart(2, "0");
  const yyyy = m[3];
  return `${yyyy}-${mm}-${dd}`;
};

const isoToDmy = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  const [y, m, d] = s.split("-"); // yyyy-mm-dd
  if (!y || !m || !d) return s;
  return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
};

const fmtFecha = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return "";
  const [y, m, d] = String(yyyy_mm_dd).split("-");
  if (!y || !m || !d) return yyyy_mm_dd;
  return `${d}/${m}/${y}`;
};

const csvToArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const toMessage = (err) => {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err?.message && typeof err.message === "string") return err.message;

  if (typeof err === "object") {
    try {
      const parts = Object.entries(err).map(([k, v]) => {
        if (Array.isArray(v)) return `${k}: ${v.join(", ")}`;
        if (typeof v === "object" && v !== null) return `${k}: ${toMessage(v)}`;
        return `${k}: ${String(v)}`;
      });
      const s = parts.join(" · ").trim();
      return s || JSON.stringify(err);
    } catch (_) {
      return JSON.stringify(err);
    }
  }
  return String(err);
};

const listToNames = (row, keys, dict) => {
  for (const k of keys) {
    const v = row?.[k];

    if (Array.isArray(v) && v.length && typeof v[0] === "object") {
      return v
        .map((x) => x?.nombre ?? x?.name ?? dict[x?.id] ?? x?.id)
        .filter(Boolean);
    }
    if (Array.isArray(v) && v.length && (typeof v[0] === "string" || typeof v[0] === "number")) {
      return v.map((id) => dict[id] ?? dict[String(id)] ?? String(id)).filter(Boolean);
    }
    if (typeof v === "string" && v.includes(",")) {
      const ids = csvToArray(v);
      return ids.map((id) => dict[id] ?? dict[String(id)] ?? String(id)).filter(Boolean);
    }
  }
  return [];
};

const isAdmin = (r) => (r?.rol ?? "").toUpperCase() === "ADMIN";

const isRolVacio = (r) => {
  const val = (r?.rol ?? "").toString().trim();
  return val === "";
};

// Meta de Page<>
const getPageMeta = (data) => ({
  content: Array.isArray(data?.content)
    ? data.content
    : Array.isArray(data)
      ? data
      : [],
  totalElements:
    typeof data?.totalElements === "number"
      ? data.totalElements
      : typeof data?.total_elements === "number"
        ? data.total_elements
        : 0,
  number:
    typeof data?.number === "number"
      ? data.number
      : typeof data?.page === "number"
        ? data.page
        : 0,
  size:
    typeof data?.size === "number"
      ? data.size
      : typeof data?.page_size === "number"
        ? data.page_size
        : 0,
});

// CSV builder
const toCSV = (rows, columns, dicts) => {
  const {
    paisesDict,
    situacionesDict,
    titulacionesDict,
    lugaresDict,
    ambitosDict,
    especialidadesDict,
  } = dicts;

  const visibleCols = columns.filter((c) => c.field !== "acciones");
  const header = visibleCols.map((c) => c.headerName).join(";");

  const listToNamesCSV = (row, keys, dict) => {
    for (const k of keys) {
      const v = row?.[k];
      if (Array.isArray(v) && v.length && typeof v[0] === "object") {
        return v
          .map((x) => x?.nombre ?? x?.name ?? dict[x?.id] ?? x?.id)
          .filter(Boolean);
      }
      if (Array.isArray(v) && v.length && (typeof v[0] === "number" || typeof v[0] === "string")) {
        return v.map((id) => dict[id] ?? dict[String(id)] ?? String(id)).filter(Boolean);
      }
      if (typeof v === "string" && v.includes(",")) {
        return csvToArray(v).map((id) => dict[id] ?? dict[String(id)] ?? String(id)).filter(Boolean);
      }
    }
    return [];
  };

  const lines = rows.map((r) =>
    visibleCols
      .map((c) => {
        let val = r[c.field];

        if (c.field === "fecha_nacimiento")
          val = r?.fecha_nacimiento ?? r?.fecha_Nacimiento ?? "";
        if (c.field === "fecha_1a_colegiacion")
          val = r?.fecha_1a_colegiacion ?? r?.fecha_1a_Colegiacion ?? "";

        if (c.field === "nacionalidad") {
          const v = r?.nacionalidad;
          const key = isNaN(Number(v)) ? v : Number(v);
          val = paisesDict[key] || v || "";
        }
        if (c.field === "pais") {
          const v = r?.pais;
          const key = isNaN(Number(v)) ? v : Number(v);
          val = paisesDict[key] || v || "";
        }
        if (c.field === "situacion_profesional") {
          const sp = r?.situacion_profesional;
          val =
            typeof sp === "object"
              ? sp?.nombre ?? ""
              : situacionesDict[isNaN(Number(sp)) ? sp : Number(sp)] || "";
        }
        if (c.field === "titulacion_principal") {
          const tp = r?.titulacion_principal;
          val =
            typeof tp === "object"
              ? tp?.nombre ?? ""
              : titulacionesDict[isNaN(Number(tp)) ? tp : Number(tp)] || "";
        }
        if (c.field === "estado") {
          val = r?.estado?.nombre ?? r?.estado ?? "";
        }
        if (c.field === "moroso") {
          const v = r?.moroso;
          val = v === 1 || v === "1" || v === true ? "Sí" : "No";
        }
        if (c.field === "remesable") {
          const v = r?.remesable;
          val = v === 1 || v === "1" || v === true ? "Sí" : "No";
        }

        if (c.field === "lugares_trabajo") {
          val = listToNamesCSV(
            r,
            ["lugares_trabajo", "lugaresTrabajoIds", "lugares_trabajo_ids"],
            lugaresDict
          ).join(", ");
        }
        if (c.field === "ambitos_trabajo") {
          val = listToNamesCSV(
            r,
            ["ambitos_trabajo", "ambitosTrabajoIds", "ambitos_trabajo_ids"],
            ambitosDict
          ).join(", ");
        }
        if (c.field === "especialidades") {
          val = listToNamesCSV(
            r,
            ["especialidades", "especialidadesIds", "especialidades_ids"],
            especialidadesDict
          ).join(", ");
        }

        if (c.field === "fecha_nacimiento" || c.field === "fecha_1a_colegiacion") {
          const s = val ? String(val) : "";
          const [y, m, d] = s.split("-");
          const ddmmyyyy = y && m && d ? `${d}/${m}/${y}` : s;
          return `"${ddmmyyyy}"`;
        }

        const s = val == null ? "" : String(val);
        return `"${s.replace(/"/g, '""')}"`;
      })
      .join(";")
  );

  return "\uFEFF" + [header, ...lines].join("\n");
};

const schemaNuevo = Yup.object({
  nombre: Yup.string().trim().min(2, "Mínimo 2 caracteres").required("Obligatorio"),
  apellido_1: Yup.string().trim().nullable(),
  apellido_2: Yup.string().trim().nullable(),
  nif: Yup.string().trim().nullable(),
  email: Yup.string().trim().email("Email no válido").required("Obligatorio"),
  password: Yup.string().min(8, "Mínimo 8 caracteres").required("Obligatorio"),
  fecha_nacimiento: Yup.string().trim().nullable(),
});
const ALTAS_IDS = [1, 3, 4, 5, 6];
const BAJAS_IDS = [7, 8, 9, 10, 11, 12];
const repeatParamsSerializer = (params) => {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (Array.isArray(v)) v.forEach(it => usp.append(k, it));
    else if (v != null && v !== "") usp.append(k, v);
  }
  return usp.toString();
};

export default function ColegiadoTable({ onEdit }) {
  const pendientesReq = useRef(0);
  const [selectedCodes, setSelectedCodes] = useState(() => new Set());
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [openINE, setOpenINE] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editCodigo, setEditCodigo] = useState(null);
  const openEdit = (row) => {
    setEditCodigo(row?.codigo);
    setEditOpen(true);
  };
  const closeEdit = () => setEditOpen(false);
  const [formError, setFormError] = useState("");

  // Bulk edit (NUEVO)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState(() => []); // array de códigos seleccionados

  // Data (server mode)
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Grid (server-side control)
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortModel, setSortModel] = useState([{ field: "codigo", sort: "asc" }]);

  // --- NUEVO: estado para el indicador de pendientes ---
  const [pendientesMap, setPendientesMap] = useState({}); // { [codigo]: boolean }

  // --- NUEVO: fetch (memorizado) ---
  const fetchPendientesForCodes = useCallback(
    async (codigos) => {
      const myReq = ++pendientesReq.current;
      const clean = (codigos || []).filter((c) => c != null);
      if (!clean.length) {
        if (myReq === pendientesReq.current) setPendientesMap({});
        return;
      }

      try {
        // 1) intento BULK
        const body = { codigos: clean, estados: ["PENDIENTE", "PENDIENTE_DEVUELTO"] };
        const { data } = await axios.post(`${API}/api/recibos/pending/map`, body, { headers });
        if (data && typeof data === "object") {
          const mapped = {};
          for (const k of Object.keys(data)) {
            const v = data[k];
            mapped[Number(k)] = !!(v === true || v === "true" || (typeof v === "number" && v > 0));
          }
          if (myReq === pendientesReq.current) setPendientesMap(mapped);
          return;
        }
      } catch (_) {
        // fallback abajo
      }

      // 2) fallback por colegiado (2 llamadas tamaño mínimo)
      try {
        const results = await Promise.all(
          clean.map(async (codigo) => {
            try {
              const [r1, r2] = await Promise.all([
                axios.get(`${API}/api/recibos`, {
                  headers,
                  params: { colegiado: codigo, resultadoBanco: "PENDIENTE", page: 0, size: 1 },
                }),
                axios.get(`${API}/api/recibos`, {
                  headers,
                  params: { colegiado: codigo, resultadoBanco: "PENDIENTE_DEVUELTO", page: 0, size: 1 },
                }),
              ]);

              const hasAny = (res) => {
                const d = res?.data;
                if (Array.isArray(d)) return d.length > 0;
                if (d && typeof d.totalElements === "number") return d.totalElements > 0;
                if (d && typeof d.total_elements === "number") return d.total_elements > 0;
                return false;
              };

              return [codigo, hasAny(r1) || hasAny(r2)];
            } catch {
              return [codigo, false];
            }
          })
        );

        if (myReq === pendientesReq.current) {
          setPendientesMap(Object.fromEntries(results));
        }
      } catch {
        if (myReq === pendientesReq.current) setPendientesMap({});
      }
    },
    [API, headers]
  );

  // --- NUEVO: recalcular cuando cambian las filas visibles ---
  useEffect(() => {
    const codigos = (rows || []).map((r) => r?.codigo).filter(Boolean);
    setPendientesMap({});
    fetchPendientesForCodes(codigos);
  }, [rows, fetchPendientesForCodes]);

  // Filtros (BORRADOR + APLICADOS)
  const emptyFilters = {
    codigo: "",
    nombre: "",
    apellido_1: "",
    apellido_2: "",
    nif: "",
    estadoId: "",
    estadoGrupo: "",
    moroso: "",
    remesable: "",
    especialidadIds: [],
    ambitoIds: [],
    lugarIds: [],
    match: "any",
    rolPendiente: "",
    onlyConPendientes: false,
  };

  const ALTAS_IDS = [1, 3, 4, 5, 6];
  const BAJAS_IDS = [7, 8, 9, 10, 11, 12];

  const [filtersDraft, setFiltersDraft] = useState({ ...emptyFilters });
  const [filtersApplied, setFiltersApplied] = useState({ ...emptyFilters });
  const [colVisibility, setColVisibility] = useState({});

  // catálogos
  const [paisesDict, setPaisesDict] = useState({});
  const [situacionesDict, setSituacionesDict] = useState({});
  const [titulacionesDict, setTitulacionesDict] = useState({});
  const [estados, setEstados] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [especialidadesDict, setEspecialidadesDict] = useState({});
  const [lugares, setLugares] = useState([]);
  const [lugaresDict, setLugaresDict] = useState({});
  const [ambitos, setAmbitos] = useState([]);
  const [ambitosDict, setAmbitosDict] = useState({});

  // ===== Helpers & estado locales SOLO dentro del componente, antes del return =====
  const [archivosNuevo, setArchivosNuevo] = React.useState({});
  const [generando, setGenerando] = React.useState(false);
  const esVentanilla = (v) => (v || "").toUpperCase() === "VENTANILLA";

  // Desglosa IBAN (ESkk + 20 CCC)
  const extraerDatosIban = (iban) => {
    const limpio = (iban || "").replace(/\s+/g, "").toUpperCase();
    const banco = limpio.slice(4, 8);
    const sucursal_Bancaria = limpio.slice(8, 12);
    const digito_Control = limpio.slice(12, 14);
    const cuenta_Bancaria = limpio.slice(14, 24); // 10 dígitos
    return { banco, sucursal_Bancaria, digito_Control, cuenta_Bancaria, ibanLimpio: limpio };
  };

  // === SUBIDA DE DOCUMENTOS (ADMIN en área del colegiado) ===
  // Busca el código del colegiado por NIF desde el admin/buscar
  async function buscarCodigoPorNif(nif) {
    const clean = (nif || "").toString().trim().toUpperCase();
    if (!clean) return null;
    const params = { page: 0, size: 1, sortBy: "codigo", order: "asc", nif: clean };
    const { data } = await axios.get(`${API}/api/admin/buscar`, { headers, params });
    const meta = getPageMeta(data);
    const arr = Array.isArray(meta.content) ? meta.content : [];
    const match = arr.find((r) => (r?.nif || "").toString().toUpperCase() === clean);
    // En tu back el identificador visible es "codigo"
    return match?.codigo ?? null;
  }

  // Sube TODOS los archivos seleccionados al área del colegiado (ADMIN)
  async function subirDocumentosTrasAlta(colegiadoCodigo, archivosMapa) {
    for (const [clave, file] of Object.entries(archivosMapa)) {
      if (!file) continue;

      const ext = (file.name?.split(".").pop() || "pdf").toLowerCase();
      const nombreDeseado = `${nombreArchivos[clave]}.${ext}`;

      const fd = new FormData();
      // 3er parámetro fuerza el nombre final del archivo en el servidor
      fd.append("file", file, nombreDeseado);
      fd.append("colegiadoId", String(colegiadoCodigo));

      await axios.post(`${API}/api/documentos/admin/subir`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
    }
  }

  // Crea Mandato TMP público (idempotente por (nif, iban))
  async function crearMandatoTmpPublico({ colegiadoCodigo = null, nif, iban, bic, fechaFirma, docSepaId = null }) {
    const body = { colegiadoCodigo, nif, iban, bic, fechaFirma, docSepaId };
    const { data } = await axios.post(`${API}/api/mandatos/tmp/publico`, body, { headers });
    const mndtId = data?.mandatoId ?? data?.mndtId ?? data?.mndt_id ?? null;
    return { mndtId, raw: data };
  }

  // Preview de mndt_id (no guarda)
  async function previewMandatoId(nif, iban, bic) {
    try {
      const { data } = await axios.get(`${API}/api/mandatos/preview`, {
        params: { nif, iban, bic },
        headers,
      });
      return data?.mandatoId || null;
    } catch {
      return null;
    }
  }

  // Etiquetas y nombres de archivos
  const etiquetasArchivos = {
    solicitud: "Solicitud de Alta Firmada Electrónicamente (PDF)",
    dni: "DNI/NIE",
    titulo: "Título de Enfermería o Certificación Supletoria Provisional",
    certificacion: "Certificación académica de notas",
    autorizacion: "Autorización para consultar Registro Nacional de Títulos Universitarios (PDF)",
    pago: "Justificante de pago",
    sepa: "Autorización de domiciliación bancaria (PDF)",
    foto: "Foto tamaño carnet",
  };
  const nombreArchivos = {
    solicitud: "solicitud_alta",
    dni: "dni_nie",
    titulo: "titulo_enfermeria",
    certificacion: "certificado_notas",
    autorizacion: "autorizacion_consulta_titulos",
    pago: "justificante_pago",
    sepa: "sepa",
    foto: "foto",
  };

  // Handler de inputs file
  const handleFileChange = (e, nombreCampo) => {
    setArchivosNuevo((prev) => ({
      ...prev,
      [nombreCampo]: e.target.files[0],
    }));
  };

  // ====== Generadores de PDFs (usan imports ya declarados arriba del archivo) ======
  async function pdfSolicitud(values) {
    const existingPdfBytes = await fetch("/Solicitud_ingreso.pdf").then((r) => r.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const firstPage = pdfDoc.getPages()[0];
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const draw = (t, x, y, size = 10) =>
      t != null && firstPage.drawText(String(t), { x, y, size, font, color: rgb(0, 0, 0) });

    const hoy = new Date();
    const meses = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
    ];

    draw(`${values.nombre || ""} ${values.apellido_1 || ""} ${values.apellido_2 || ""}`.trim(), 112.07, 707);
    draw(values.localidad_nacimiento || "", 123.56, 692);
    draw(values.provincia_nacimiento || values.provincia || "", 305.12, 692);
    if (values.fecha_nacimiento) {
      const f = new Date(values.fecha_nacimiento);
      draw(`${String(f.getDate()).padStart(2, "0")}/${String(f.getMonth() + 1).padStart(2, "0")}/${f.getFullYear()}`, 457, 692);
    }
    draw(values.nif || "", 140.2, 675);
    draw(values.direccion || "", 290.74, 675);
    draw(values.numero || "", 469, 675);
    draw(values.piso || "", 95, 659);
    draw(values.poblacion || "", 195, 659);
    draw(values.provincia || "", 400, 659);
    draw(values.cp || "", 95, 643);
    draw(values.telefono || "", 185, 642);
    draw(values.email || "", 322, 640);
    draw(values.universidad || values.titulacion || "", 273, 623);
    draw(values.ano_fin || "", 122, 606);
    draw(values.entidad_bancaria || "", 254, 568);
    draw(values.iban || "", 130, 552);

    draw(String(hoy.getDate()), 314, 149);
    draw(meses[hoy.getMonth()], 353, 149);
    draw(String(hoy.getFullYear()), 487, 149);

    const pdfBytes = await pdfDoc.save();
    saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "Solicitud_Ingreso.pdf");
  }

  async function pdfRegistro(values) {
    const existingPdfBytes = await fetch("/registro_nacional.pdf").then((r) => r.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const firstPage = pdfDoc.getPages()[0];
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const draw = (t, x, y, size = 12) =>
      t != null && firstPage.drawText(String(t), { x, y, size, font, color: rgb(0, 0, 0) });

    const hoy = new Date();
    const meses = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
    ];

    draw(`${values.nombre || ""} ${values.apellido_1 || ""} ${values.apellido_2 || ""}`.trim(), 140, 693);
    draw(values.nif || "", 140, 665);
    const direccion = `${values.direccion || ""}`.trim();
    const poblacion = values.poblacion ? `en ${values.poblacion}` : "";
    draw(`${direccion} ${poblacion}`.trim(), 160, 637);
    draw(values.telefono || "", 140, 610);
    draw(values.email || "", 200, 580);

    draw(String(hoy.getDate()), 340, 282);
    draw(meses[hoy.getMonth()], 395, 282);
    draw(String(hoy.getFullYear()).slice(-2), 522, 282);

    const pdfBytes = await pdfDoc.save();
    saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "Registro_Nacional.pdf");
  }

  async function pdfSepa(values, mandatoIdParaPdf) {
    const existingPdfBytes = await fetch("/sepa.pdf").then((r) => r.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const firstPage = pdfDoc.getPages()[0];
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const draw = (t, x, y, size = 10) =>
      t != null && firstPage.drawText(String(t), { x, y, size, font, color: rgb(0, 0, 0) });

    const nombreCompleto = `${(values.nombre || "").toUpperCase()} ${(values.apellido_1 || "").toUpperCase()} ${(values.apellido_2 || "").toUpperCase()}`.trim();
    const linea1 = `${(values.direccion || "").toUpperCase()}`.trim();
    const linea2 = `${(values.cp || "").toUpperCase()} ${(values.poblacion || "").toUpperCase()} ${(values.provincia || "").toUpperCase()}`.trim();

    const { banco, ibanLimpio } = extraerDatosIban(values.iban || "");
    const bic = obtenerBICdesdeBanco(banco);

    // Cabecera derecha
    draw(nombreCompleto, 340, 760);
    draw(linea1, 340, 745);
    draw(linea2, 340, 730);
    draw((values.pais || "").toUpperCase(), 340, 715);
    draw(`Tel.: ${values.telefono || ""}`, 340, 700);
    draw(values.email || "", 340, 685);

    // mndt_id
    draw(`${mandatoIdParaPdf || "SIN ID DISPONIBLE"}`, 299, 604);

    // Bloque inferior / banco
    draw(nombreCompleto, 340, 300);
    draw(linea1, 340, 265);
    draw(linea2, 340, 250);
    draw((values.pais || "").toUpperCase(), 340, 235);
    draw(banco, 66, 185);
    draw(bic || "", 170, 185);
    draw(ibanLimpio, 300, 185);

    const hoy = dayjs();
    draw(hoy.format("DD/MM/YYYY"), 200, 134);

    const pdfBytes = await pdfDoc.save();
    saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "SEPA.pdf");
  }

  // UI
  const [openNuevo, setOpenNuevo] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const notify = (msg, sev = "success") =>
    setSnack({ open: true, msg: toMessage(msg), sev });

  const setMoroso = async (row, nuevo) => {
    if (!row?.codigo) return;
    try {
      setRows((prev) =>
        prev.map((r) => (r.codigo === row.codigo ? { ...r, moroso: String(nuevo) } : r))
      );
      await axios.put(
        `${API}/api/admin/colegiados/${row.codigo}`,
        { moroso: String(nuevo) },
        { headers }
      );
      notify(nuevo === "1" ? "Marcado como MOROSO" : "Marcado como NO MOROSO");
      fetchPage(page, pageSize, sortModel, filtersApplied);
    } catch (e) {
      console.error(e);
      const raw = e?.response?.data ?? e?.message ?? e;
      notify(raw || "No se pudo actualizar el estado de moroso", "error");
      fetchPage(page, pageSize, sortModel, filtersApplied);
    }
  };

  const setRemesable = async (row, nuevo) => {
    if (!row?.codigo) return;
    try {
      setRows((prev) =>
        prev.map((r) => (r.codigo === row.codigo ? { ...r, remesable: String(nuevo) } : r))
      );
      await axios.put(
        `${API}/api/admin/colegiados/${row.codigo}`,
        { remesable: String(nuevo) },
        { headers }
      );
      notify(nuevo === "1" ? "Marcado como REMESABLE" : "Marcado como NO REMESABLE");
      fetchPage(page, pageSize, sortModel, filtersApplied);
    } catch (e) {
      console.error(e);
      const raw = e?.response?.data ?? e?.message ?? e;
      notify(raw || "No se pudo actualizar el estado de remesable", "error");
      fetchPage(page, pageSize, sortModel, filtersApplied);
    }
  };


  // ------------ Catálogos ------------
  useEffect(() => {
    let alive = true;

    const toArray = (resData) => {
      if (!resData) return [];
      if (Array.isArray(resData)) return resData;
      if (Array.isArray(resData?.content)) return resData.content;
      if (Array.isArray(resData?.data)) return resData.data;
      return [];
    };

    const toDict = (arr, key = "id", valA = "nombre", valB = "name") =>
      (Array.isArray(arr) ? arr : []).reduce((acc, it) => {
        const id = it?.[key];
        if (id != null) {
          acc[id] = it?.[valA] ?? it?.[valB] ?? "";
        }
        return acc;
      }, {});

    (async () => {
      try {
        const [resP, resS, resT, resE, resEsp, resLT, resAT] = await Promise.all([
          axios.get(`${API}/api/paises`, { headers }),
          axios.get(`${API}/api/situaciones-profesionales`, { headers }),
          axios.get(`${API}/api/titulaciones-principales`, { headers }),
          axios.get(`${API}/api/estados`, { headers }),
          axios.get(`${API}/api/especialidades`, { headers }),
          axios.get(`${API}/api/lugares-trabajo`, { headers }),
          axios.get(`${API}/api/ambitos-trabajo`, { headers }),
        ]);
        if (!alive) return;

        const P = toArray(resP?.data);
        const S = toArray(resS?.data);
        const T = toArray(resT?.data);
        const E = toArray(resE?.data);
        const ESP = toArray(resEsp?.data);
        const LUG = toArray(resLT?.data);
        const AMB = toArray(resAT?.data);

        setPaisesDict(toDict(P));
        setSituacionesDict(toDict(S));
        setTitulacionesDict(toDict(T));

        setEstados(E);

        setEspecialidades(ESP);
        setEspecialidadesDict(toDict(ESP));

        setLugares(LUG);
        setLugaresDict(toDict(LUG));

        setAmbitos(AMB);
        setAmbitosDict(toDict(AMB));
      } catch (e) {
        console.warn("Catálogos no disponibles; se mostrará valor crudo.", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [API, headers]);

  // ------------ Fetch (server-side) ------------
  const fetchPage = async (p, size, sortM, filters) => {
    setLoading(true);
    try {
      const sortOrder = sortM?.[0]?.sort || "asc";

      const hasAnyFilter = Object.values({
        codigo: filters.codigo,
        nombre: filters.nombre,
        apellido_1: filters.apellido_1,
        apellido_2: filters.apellido_2,
        nif: filters.nif,
        estadoId: filters.estadoId,
        estadoGrupo: filters.estadoGrupo,
        moroso: filters.moroso,
        remesable: filters.remesable,
        espLen: Array.isArray(filters.especialidadIds) ? filters.especialidadIds.length : 0,
        ambLen: Array.isArray(filters.ambitoIds) ? filters.ambitoIds.length : 0,
        lugLen: Array.isArray(filters.lugarIds) ? filters.lugarIds.length : 0,
        rolPendiente: filters.rolPendiente,
      }).some((v) => (v ?? "").toString().trim() !== "" && v !== 0);

      let res;

      if (hasAnyFilter) {
        const hasTextFilters = ["codigo", "nombre", "apellido_1", "apellido_2", "nif"]
          .some((k) => (filters[k] ?? "").toString().trim() !== "");

        const hasSelectFilters =
          (filters.estadoId ?? "") !== "" ||
          (filters.estadoGrupo ?? "") !== "" ||
          (filters.moroso ?? "") !== "" ||
          (filters.remesable ?? "") !== "" ||
          (filters.rolPendiente ?? "") !== "" ||
          (Array.isArray(filters.especialidadIds) && filters.especialidadIds.length > 0) ||
          (Array.isArray(filters.ambitoIds) && filters.ambitoIds.length > 0) ||
          (Array.isArray(filters.lugarIds) && filters.lugarIds.length > 0);

        // construir params una sola vez aquí
        const params = { page: p, size, sortBy: "codigo", order: sortOrder };

        const add = (k, v) => {
          if ((v ?? "").toString().trim() !== "") params[k] = v.toString().trim();
        };
        const cod = (filters.codigo ?? "").toString().trim();
        if (/^\d+$/.test(cod)) params.codigo = Number(cod);
        add("nombre", filters.nombre);
        add("apellido_1", filters.apellido_1);
        add("apellido_2", filters.apellido_2);
        add("nif", filters.nif);

        // === ESTADOS (prioridad: estadoId > grupo ALTAS/BAJAS) ===
        if (filters.estadoId) {
          params.estadoId = [String(filters.estadoId)];     // -> estadoId=XX (repetible si array)
        } else if ((filters.estadoGrupo || "").trim() !== "") {
          const ids = filters.estadoGrupo === "ALTAS" ? ALTAS_IDS : BAJAS_IDS;
          params.estadoId = ids.map(String);                // -> estadoId=1&estadoId=3&...
        }

        // otros selects
        if (filters.moroso) params.moroso = filters.moroso;
        if (filters.remesable) params.remesable = filters.remesable;
        if (Array.isArray(filters.especialidadIds) && filters.especialidadIds.length) {
          params.especialidadId = filters.especialidadIds.join(",");
        }
        if (Array.isArray(filters.ambitoIds) && filters.ambitoIds.length) {
          params.ambitoId = filters.ambitoIds.join(",");
        }
        if (Array.isArray(filters.lugarIds) && filters.lugarIds.length) {
          params.lugarTrabajoId = filters.lugarIds.join(",");
        }
        if (hasSelectFilters) {
          params.match = filters.match || "any";
        }
        if (filters.rolPendiente === "PENDIENTE") {
          params.rol = "PENDIENTE";
        }

        res = await axios.get(`${API}/api/admin/buscar`, {
          headers,
          params,
          paramsSerializer: repeatParamsSerializer, // necesario para repetir estadoId
        });

        const meta = getPageMeta(res.data);
        const content = meta.content.filter((r) => !isAdmin(r));
        setRows(content);
        setRowCount(meta.totalElements);
      } else {
        // sin filtros -> listado base
        const params = { page: p, size, sortBy: "codigo", order: sortOrder, sinAdmins: true };
        if (filters.rolPendiente === "PENDIENTE") params.rol = "PENDIENTE";

        res = await axios.get(`${API}/api/admin/colegiados`, { headers, params });

        const meta = getPageMeta(res.data);
        setRows(meta.content);
        setRowCount(meta.totalElements);
      }
    } catch (err) {
      console.error("Error cargando colegiados:", err);
      setRows([]);
      setRowCount(0);
      notify("No se pudo cargar el listado de colegiados", "error");
    } finally {
      setLoading(false);
    }
  }; // ← cierre de fetchPage


  // ------------ Filtrado visual (tras cargar datos) ------------
  const displayedRows = useMemo(() => {
    let arr = rows;

    // Filtro por rol pendiente (solo afecta a lo que se muestra)
    if (filtersApplied.rolPendiente === "PENDIENTE") {
      arr = arr.filter(
        (r) => (r?.rol ?? "").toString().trim().toUpperCase() === "PENDIENTE"
      );
    }

    // Filtro visual por “solo con € pendientes”
    if (filtersApplied.onlyConPendientes) {
      arr = arr.filter((r) => !!pendientesMap[r?.codigo]);
    }

    return arr;
  }, [rows, filtersApplied.rolPendiente, filtersApplied.onlyConPendientes, pendientesMap]);


  // ------------ Carga inicial y recargas automáticas ------------
  useEffect(() => {
    fetchPage(page, pageSize, sortModel, filtersApplied);
  }, [page, pageSize, sortModel, filtersApplied]);

  // =================== EXPORT: traer todo (todas las páginas) o solo seleccionados ===================

  // Construye los params EXACTOS como fetchPage (pero sin page/size fijos)
  const buildParamsForExport = (filters, sortM, p, size) => {
    const sortOrder = sortM?.[0]?.sort || "asc";

    // mismo criterio que fetchPage
    const hasAnyFilter = Object.values({
      codigo: filters.codigo,
      nombre: filters.nombre,
      apellido_1: filters.apellido_1,
      apellido_2: filters.apellido_2,
      nif: filters.nif,
      estadoId: filters.estadoId,
      estadoGrupo: filters.estadoGrupo,
      moroso: filters.moroso,
      remesable: filters.remesable,
      espLen: Array.isArray(filters.especialidadIds) ? filters.especialidadIds.length : 0,
      ambLen: Array.isArray(filters.ambitoIds) ? filters.ambitoIds.length : 0,
      lugLen: Array.isArray(filters.lugarIds) ? filters.lugarIds.length : 0,
      rolPendiente: filters.rolPendiente,
    }).some((v) => (v ?? "").toString().trim() !== "" && v !== 0);

    const params = { page: p, size, sortBy: "codigo", order: sortOrder };

    const add = (k, v) => {
      if ((v ?? "").toString().trim() !== "") params[k] = v.toString().trim();
    };

    const cod = (filters.codigo ?? "").toString().trim();
    if (/^\d+$/.test(cod)) params.codigo = Number(cod);

    add("nombre", filters.nombre);
    add("apellido_1", filters.apellido_1);
    add("apellido_2", filters.apellido_2);
    add("nif", filters.nif);

    // ESTADOS (prioridad: estadoId > grupo)
    if (filters.estadoId) {
      params.estadoId = [String(filters.estadoId)];
    } else if ((filters.estadoGrupo || "").trim() !== "") {
      const ids = filters.estadoGrupo === "ALTAS" ? ALTAS_IDS : BAJAS_IDS;
      params.estadoId = ids.map(String);
    }

    if (filters.moroso) params.moroso = filters.moroso;
    if (filters.remesable) params.remesable = filters.remesable;

    if (Array.isArray(filters.especialidadIds) && filters.especialidadIds.length) {
      params.especialidadId = filters.especialidadIds.join(",");
    }
    if (Array.isArray(filters.ambitoIds) && filters.ambitoIds.length) {
      params.ambitoId = filters.ambitoIds.join(",");
    }
    if (Array.isArray(filters.lugarIds) && filters.lugarIds.length) {
      params.lugarTrabajoId = filters.lugarIds.join(",");
    }

    // match solo si hay selects/listas (igual que tu fetchPage)
    const hasSelectFilters =
      (filters.estadoId ?? "") !== "" ||
      (filters.estadoGrupo ?? "") !== "" ||
      (filters.moroso ?? "") !== "" ||
      (filters.remesable ?? "") !== "" ||
      (filters.rolPendiente ?? "") !== "" ||
      (Array.isArray(filters.especialidadIds) && filters.especialidadIds.length > 0) ||
      (Array.isArray(filters.ambitoIds) && filters.ambitoIds.length > 0) ||
      (Array.isArray(filters.lugarIds) && filters.lugarIds.length > 0);

    if (hasSelectFilters) params.match = filters.match || "any";

    if (filters.rolPendiente === "PENDIENTE") params.rol = "PENDIENTE";

    return { hasAnyFilter, params };
  };

  // Trae una página (para export) usando el mismo endpoint que fetchPage
  const fetchExportPage = async (p, size, sortM, filters) => {
    const { hasAnyFilter, params } = buildParamsForExport(filters, sortM, p, size);

    if (hasAnyFilter) {
      const res = await axios.get(`${API}/api/admin/buscar`, {
        headers,
        params,
        paramsSerializer: repeatParamsSerializer,
      });
      const meta = getPageMeta(res.data);
      // igual que tu fetchPage: fuera admins
      return {
        content: meta.content.filter((r) => !isAdmin(r)),
        total: meta.totalElements,
      };
    }

    // sin filtros
    const params2 = { ...params, sinAdmins: true };
    const res = await axios.get(`${API}/api/admin/colegiados`, { headers, params: params2 });
    const meta = getPageMeta(res.data);
    return { content: meta.content, total: meta.totalElements };
  };

  // Calcula “tiene pendientes” por lotes con tu endpoint bulk
  const filterOnlyConPendientes = async (allRows) => {
    if (!Array.isArray(allRows) || allRows.length === 0) return [];

    const chunkSize = 400; // ajusta si quieres
    const out = [];

    for (let i = 0; i < allRows.length; i += chunkSize) {
      const chunk = allRows.slice(i, i + chunkSize);
      const codigos = chunk.map((r) => r?.codigo).filter(Boolean);
      if (!codigos.length) continue;

      try {
        const body = { codigos, estados: ["PENDIENTE", "PENDIENTE_DEVUELTO"] };
        const { data } = await axios.post(`${API}/api/recibos/pending/map`, body, { headers });

        // data: { [codigo]: true/false o num }
        const has = (c) => {
          const v = data?.[String(c)] ?? data?.[Number(c)];
          return !!(v === true || v === "true" || (typeof v === "number" && v > 0));
        };

        chunk.forEach((r) => {
          if (has(r.codigo)) out.push(r);
        });
      } catch (e) {
        // si falla, por seguridad NO incluimos el chunk (para respetar el filtro)
        console.warn("No se pudo calcular pendientes para export en chunk", e);
      }
    }

    return out;
  };

  // ✅ LA FUNCIÓN QUE TE FALTABA
  const fetchAllForExport = async (filters) => {
    // 1) Si hay seleccionados => exportar SOLO esos
    if (selectedCodes.size > 0) {
      const codes = Array.from(selectedCodes);

      // Intento 1: si están en memoria (rows/displayedRows), úsalos
      const mapLocal = new Map();
      [...rows, ...displayedRows].forEach((r) => {
        if (r?.codigo != null) mapLocal.set(Number(r.codigo), r);
      });

      // Para los que no estén cargados, hacemos fetch por código
      const missing = codes.filter((c) => !mapLocal.has(Number(c)));

      if (missing.length) {
        const concurrency = 8;
        const fetched = [];
        for (let i = 0; i < missing.length; i += concurrency) {
          const batch = missing.slice(i, i + concurrency);
          const res = await Promise.all(
            batch.map(async (c) => {
              try {
                const { data } = await axios.get(`${API}/api/admin/colegiados/${c}`, { headers });
                return data ?? null;
              } catch (e) {
                console.warn("No se pudo cargar colegiado para export", c, e);
                return null;
              }
            })
          );
          res.filter(Boolean).forEach((r) => fetched.push(r));
        }
        fetched.forEach((r) => {
          if (r?.codigo != null) mapLocal.set(Number(r.codigo), r);
        });
      }

      const selectedRows = codes
        .map((c) => mapLocal.get(Number(c)))
        .filter(Boolean)
        .filter((r) => !isAdmin(r));

      // Si el filtro visual de rol pendiente está activo, respétalo también en export
      if (filters?.rolPendiente === "PENDIENTE") {
        return selectedRows.filter(
          (r) => (r?.rol ?? "").toString().trim().toUpperCase() === "PENDIENTE"
        );
      }

      // Si el filtro “solo con pendientes” está activo, filtra también
      if (filters?.onlyConPendientes) {
        return await filterOnlyConPendientes(selectedRows);
      }

      return selectedRows;
    }

    // 2) Si NO hay seleccionados => exportar TODO lo que cumpla filtros (todas las páginas)
    const pageSizeExport = 500; // sube/baja según rendimiento
    let p = 0;
    let all = [];
    let total = 0;

    while (true) {
      const { content, total: t } = await fetchExportPage(p, pageSizeExport, sortModel, filters);
      if (p === 0) total = t;

      all = all.concat(content);

      // fin
      if (all.length >= total) break;
      if (!content.length) break;

      p += 1;
    }

    // Respeta filtro visual rolPendiente (aunque el back ya lo aplica, por seguridad)
    if (filters?.rolPendiente === "PENDIENTE") {
      all = all.filter((r) => (r?.rol ?? "").toString().trim().toUpperCase() === "PENDIENTE");
    }

    // Respeta “solo con pendientes (€)” calculándolo en bulk (para TODAS las páginas)
    if (filters?.onlyConPendientes) {
      all = await filterOnlyConPendientes(all);
    }

    return all;
  };


  const handleExport = async () => {
    try {
      const all = await fetchAllForExport(filtersApplied);

      if (!all.length) {
        notify("No hay datos para exportar", "warning");
        return;
      }

      const exportColumns = columns.filter(
        (c) => c.field !== "acciones" && colVisibility[c.field] !== false && c.hide !== true
      );

      const csv = toCSV(all, exportColumns, {
        paisesDict,
        situacionesDict,
        titulacionesDict,
        lugaresDict,
        ambitosDict,
        especialidadesDict,
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = selectedCodes.size > 0 ? "colegiados_seleccionados.csv" : "colegiados.csv";
      link.click();
    } catch (e) {
      console.error(e);
      notify(e?.response?.data ?? e?.message ?? "Error exportando", "error");
    }
  };

  // ------------ Acciones fila ------------
  const eliminarColegiado = async (row) => {
    if (!window.confirm(`¿Eliminar al colegiado ${row?.codigo} - ${row?.nombre}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/colegiados/${row.codigo}`, { headers });
      notify("Colegiado eliminado");
      fetchPage(page, pageSize, sortModel, filtersApplied);
    } catch (e) {
      console.error(e);
      notify("No se pudo eliminar el colegiado", "error");
    }
  };

  const marcarActivo = async (row) => {
    try {
      if ((row?.rol ?? "").toUpperCase() !== "PENDIENTE") return;
      await axios.put(`${API}/api/admin/colegiados/${row.codigo}/aprobar`, {}, { headers });
      notify("Colegiado aprobado: fecha de alta y recibo de 37€ creados (contenedor ALTAS).");
      fetchPage(page, pageSize, sortModel, filtersApplied);
    } catch (e) {
      console.error(e);
      notify(e?.response?.data?.error || e?.message || "No se pudo aprobar", "error");
    }
  };

  const marcarPendiente = async (row) => {
    try {
      if (!isRolVacio(row)) return; // solo desde rol vacío
      await axios.put(
        `${API}/api/admin/colegiados/${row.codigo}`,
        { rol: "PENDIENTE" },
        { headers }
      );
      notify("Colegiado marcado como PENDIENTE");
      fetchPage(page, pageSize, sortModel, filtersApplied);
    } catch (e) {
      console.error(e);
      notify("No se pudo marcar como pendiente", "error");
    }
  };

  // ------------ Columnas ------------
  const columns = [
    {
      field: "_sel",
      headerName: "",
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <Checkbox
          size="small"
          indeterminate={somePageSelected}
          checked={allPageSelected}
          onChange={toggleAllPage}
          inputProps={{ "aria-label": "Seleccionar página" }}
        />
      ),
      renderCell: ({ row }) => (
        <Checkbox
          size="small"
          checked={isRowSelected(row.codigo)}
          onChange={() => toggleRow(row.codigo)}
          inputProps={{ "aria-label": `Seleccionar ${row.codigo}` }}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "ACCIONES",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const pendiente = (row?.rol ?? "").toString().trim().toUpperCase() === "PENDIENTE";
        const rolVacio = isRolVacio(row);
        const tienePendientes = !!pendientesMap[row?.codigo];
        const tieneObservaciones = (row?.observaciones ?? "").toString().trim() !== "";

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 0.5 }}>
            {/* === Indicador € rojo en negrita si tiene recibos pendientes === */}
            {tienePendientes && (
              // opción 1: símbolo puro
              <Box component="span" sx={{ color: "error.main", fontWeight: 800, mr: 0.5 }}>
                <EuroIcon sx={{ color: "error.main", fontWeight: 800 }} fontSize="small" />
              </Box>
            )}
            {tieneObservaciones && (
              <Tooltip title={row.observaciones}>
                <ChatBubbleIcon
                  fontSize="small"
                  sx={{
                    color: "warning.main",
                    fontWeight: 800,
                    mr: 0.5,
                  }}
                />
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => openEdit(row)} aria-label="editar">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => eliminarColegiado(row)} aria-label="eliminar">
              <DeleteIcon fontSize="small" />
            </IconButton>

            {pendiente && (
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={() => marcarActivo(row)}
                title="Marcar como ACTIVO"
                aria-label="activar"
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            )}

            {rolVacio && (
              <Tooltip title="Marcar como PENDIENTE">
                <IconButton
                  size="small"
                  sx={{ p: 0.5 }}
                  onClick={() => marcarPendiente(row)}
                  aria-label="marcar-pendiente"
                >
                  <PriorityHighIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "remesable",
      headerName: "REMESABLE",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const v = params?.row?.remesable;
        const esSi = v === 1 || v === "1" || v === true;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              {esSi ? "Sí" : "No"}
            </Typography>
            <Tooltip title={esSi ? "Marcar como NO REMESABLE" : "Marcar como REMESABLE"}>
              <IconButton size="small" onClick={() => setRemesable(params.row, esSi ? "0" : "1")}>
                {esSi ? <DoDisturbOffIcon fontSize="small" /> : <PriorityHighIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    { field: "codigo", headerName: "CÓDIGO", width: 110, sortable: true },
    { field: "nombre", headerName: "NOMBRE", flex: 1, minWidth: 160, sortable: false },
    { field: "apellido_1", headerName: "APELLIDO 1", flex: 1, minWidth: 160, sortable: false },
    { field: "apellido_2", headerName: "APELLIDO 2", flex: 1, minWidth: 160, sortable: false },
    { field: "nif", headerName: "NIF", width: 140, sortable: false },
    { field: "email", headerName: "EMAIL", flex: 1.2, minWidth: 200, sortable: false },
    { field: "telefono_1", headerName: "TELÉFONO 1", width: 140, sortable: false },
    { field: "telefono_2", headerName: "TELÉFONO 2", width: 140, sortable: false },
    {
      field: "fecha_nacimiento",
      headerName: "FECHA NACIMIENTO",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => fmtFecha(row?.fecha_nacimiento ?? row?.fecha_Nacimiento ?? ""),
    },
    {
      field: "fecha_1a_colegiacion",
      headerName: "1ª COLEGIACIÓN",
      width: 160,
      sortable: false,
      renderCell: ({ row }) =>
        fmtFecha(row?.fecha_1a_colegiacion ?? row?.fecha_1a_Colegiacion ?? ""),
    },
    {
      field: "nacionalidad",
      headerName: "NACIONALIDAD",
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const v = params.row?.nacionalidad;
        if (v == null || v === "") return "";
        const key = isNaN(Number(v)) ? v : Number(v);
        return paisesDict[key] || v;
      },
    },
    {
      field: "pais",
      headerName: "PAÍS",
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const v = params.row?.pais;
        if (v == null || v === "") return "";
        const key = isNaN(Number(v)) ? v : Number(v);
        return paisesDict[key] || v;
      },
    },
    { field: "localidad", headerName: "LOCALIDAD", width: 160, sortable: false },
    { field: "c_p_", headerName: "C.P.", width: 110, sortable: false },
    { field: "provincia", headerName: "PROVINCIA", width: 160, sortable: false },
    {
      field: "sexo",
      headerName: "SEXO",
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const v = (params?.row?.sexo ?? "").toString().toUpperCase();
        if (v === "F" || v.startsWith("FEM") || v === "1") return "Femenino";
        if (v === "M" || v.startsWith("MAS") || v === "0") return "Masculino";
        return v;
      },
    },
    {
      field: "situacion_profesional",
      headerName: "SITUACIÓN PROFESIONAL",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const sp = params.row?.situacion_profesional;
        if (!sp) return "";
        if (typeof sp === "object") return sp?.nombre ?? "";
        const key = isNaN(Number(sp)) ? sp : Number(sp);
        return situacionesDict[key] || "";
      },
    },
    {
      field: "estado",
      headerName: "ESTADO",
      width: 160,
      sortable: false,
      renderCell: (params) => params?.row?.estado?.nombre ?? params?.row?.estado ?? "",
    },
    {
      field: "titulacion_principal",
      headerName: "TITULACIÓN PRINCIPAL",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const tp = params.row?.titulacion_principal;
        if (!tp) return "";
        if (typeof tp === "object") return tp?.nombre ?? "";
        const key = isNaN(Number(tp)) ? tp : Number(tp);
        return titulacionesDict[key] || "";
      },
    },
    {
      field: "lugares_trabajo",
      headerName: "LUGARES TRABAJO",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: ({ row }) => {
        const names = listToNames(
          row,
          ["lugares_trabajo", "lugaresTrabajoIds", "lugares_trabajo_ids"],
          lugaresDict
        );
        const text = names.join(", ");
        return (
          <Box
            title={text}
            sx={{
              fontSize: 12,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {text}
          </Box>
        );
      },
    },
    {
      field: "ambitos_trabajo",
      headerName: "ÁMBITOS TRABAJO",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: ({ row }) => {
        const names = listToNames(
          row,
          ["ambitos_trabajo", "ambitosTrabajoIds", "ambitos_trabajo_ids"],
          ambitosDict
        );
        const text = names.join(", ");
        return (
          <Box
            title={text}
            sx={{
              fontSize: 12,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {text}
          </Box>
        );
      },
    },
    {
      field: "especialidades",
      headerName: "ESPECIALIDADES",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: ({ row }) => {
        const names = listToNames(
          row,
          ["especialidades", "especialidadesIds", "especialidades_ids"],
          especialidadesDict
        );
        const text = names.join(", ");
        return (
          <Box
            title={text}
            sx={{
              fontSize: 12,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {text}
          </Box>
        );
      },
    },
    {
      field: "moroso",
      headerName: "MOROSO",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const v = params?.row?.moroso;
        const esSi = v === 1 || v === "1" || v === true;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              {esSi ? "Sí" : "No"}
            </Typography>
            <Tooltip title={esSi ? "Marcar como NO MOROSO" : "Marcar como MOROSO"}>
              <IconButton size="small" onClick={() => setMoroso(params.row, esSi ? "0" : "1")}>
                {esSi ? <DoDisturbOffIcon fontSize="small" /> : <PriorityHighIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // ------------ Grid ------------
  const handleSortChange = (m) => {
    const next = m?.length ? m : [{ field: "codigo", sort: "asc" }];
    const f = next[0].field;
    const s = next[0].sort || "asc";
    if (f !== "codigo") {
      setSortModel([{ field: "codigo", sort: s }]);
      return;
    }
    setSortModel(next);
    setPage(0);
  };
  const totalMinWidth = calcTotalMinWidth(columns);
  if (loading && rows.length === 0) return <CircularProgress />;

  const isRowSelected = (codigo) => selectedCodes.has(codigo);

  const toggleRow = (codigo) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  // “Seleccionar todo” SOLO de la página actual (rows)
  const allPageSelected = rows.length > 0 && rows.every(r => selectedCodes.has(r.codigo));
  const somePageSelected = rows.some(r => selectedCodes.has(r.codigo)) && !allPageSelected;

  const toggleAllPage = () => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        rows.forEach(r => next.delete(r.codigo));
      } else {
        rows.forEach(r => next.add(r.codigo));
      }
      return next;
    });
  };

  const selectionCount = selectedCodes.size;
  function calcTotalMinWidth(cols = []) {
    return cols.reduce((acc, col) => {
      const w = Number(col.minWidth ?? col.width ?? 120);
      return acc + (Number.isFinite(w) ? w : 120);
    }, 0);
  }

  return (
    <Paper>
      <Typography variant="h5" sx={{ px: 2, pt: 2, mb: 2 }}>
        Listado de Colegiados
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        pb={2}
        gap={2}
        flexWrap="wrap"
        maxWidth="80%"
      >
        {/* Filtros: un solo grid. Los selects van debajo de los inputs */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
            gap: 1,
            flexGrow: 1,
            flexBasis: "100%", // asegura que ocupa una fila completa en el contenedor flex
          }}
        >
          {/* FILA 1: Inputs de texto */}
          <TextField
            size="small"
            label="Código"
            value={filtersDraft.codigo}
            onChange={(e) => setFiltersDraft((f) => ({ ...f, codigo: e.target.value }))}
          />
          <TextField
            size="small"
            label="Nombre"
            value={filtersDraft.nombre}
            onChange={(e) => setFiltersDraft((f) => ({ ...f, nombre: e.target.value }))}
          />
          <TextField
            size="small"
            label="Apellido 1"
            value={filtersDraft.apellido_1}
            onChange={(e) => setFiltersDraft((f) => ({ ...f, apellido_1: e.target.value }))}
          />
          <TextField
            size="small"
            label="Apellido 2"
            value={filtersDraft.apellido_2}
            onChange={(e) => setFiltersDraft((f) => ({ ...f, apellido_2: e.target.value }))}
          />
          <TextField
            size="small"
            label="NIF"
            value={filtersDraft.nif}
            onChange={(e) => setFiltersDraft((f) => ({ ...f, nif: e.target.value }))}
          />

          {/* FILA 2: Selects principales (debajo de los inputs) */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              mt: 2,
              gap: 1,
              gridColumn: "1 / -1", // ocupar toda la fila del grid padre
            }}
          >
            {/* Estado */}
            <TextField
              select
              size="small"
              label="Estado"
              sx={{ minWidth: 200 }}
              value={filtersDraft.estadoId}
              onChange={(e) => setFiltersDraft((f) => ({ ...f, estadoId: e.target.value }))}
            >
              <MenuItem value="">Todos</MenuItem>
              {estados.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.nombre}
                </MenuItem>
              ))}
            </TextField>


            {/* NUEVO: Grupo de Estados (ALTAS/BAJAS) */}
            <TextField
              select
              size="small"
              label="Grupo de Estado"
              sx={{ minWidth: 200 }}
              value={filtersDraft.estadoGrupo}
              onChange={(e) => setFiltersDraft((f) => ({ ...f, estadoGrupo: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="ALTAS">ALTAS</MenuItem>
              <MenuItem value="BAJAS">BAJAS</MenuItem>
            </TextField>
            {/* ROL */}
            <TextField
              select
              size="small"
              label="Modo"
              sx={{ minWidth: 200 }}
              value={filtersDraft.rolPendiente}
              onChange={(e) => setFiltersDraft((f) => ({ ...f, rolPendiente: e.target.value }))}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
            </TextField>

            {/* Solo con € pendientes */}
            <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
              <Checkbox
                checked={!!filtersDraft.onlyConPendientes}
                onChange={(e) => {
                  setPage(0);
                  setFiltersDraft((f) => ({ ...f, onlyConPendientes: e.target.checked }));
                }
                }
              />
              <Typography variant="body2">Solo con recibos pendientes (€)</Typography>
            </Box>
            <TextField
              select
              size="small"
              label="Remesable"
              sx={{ minWidth: 200 }}
              value={filtersDraft.remesable}
              onChange={(e) => setFiltersDraft((f) => ({ ...f, remesable: e.target.value }))}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="SI">Sí</MenuItem>
              <MenuItem value="NO">No</MenuItem>
            </TextField>

            {/* Coincidencia */}
            {/*<TextField
              select
              size="small"
              label="Coincidencia (listas)"
              sx={{ minWidth: 200 }}
              value={filtersDraft.match}
              onChange={(e) => setFiltersDraft((f) => ({ ...f, match: e.target.value }))}
              helperText="CUALQUIERA / TODAS"
            >
              <MenuItem value="any">CUALQUIERA</MenuItem>
              <MenuItem value="all">TODAS</MenuItem>
            </TextField>*/}
          </Box>


          {/* FILA 3: Listas múltiples (debajo también) */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              gridColumn: "1 / -1",
            }}
          >
            {/* Especialidades */}
            <TextField
              sx={{ minWidth: 200 }}
              select
              SelectProps={{ multiple: true, renderValue: (sel) => `${sel.length} seleccionada(s)` }}
              size="small"
              label="Especialidades"
              value={filtersDraft.especialidadIds}
              onChange={(e) => {
                const val = e.target.value;
                setFiltersDraft((f) => ({
                  ...f,
                  especialidadIds: Array.isArray(val) ? val : [],
                }));
              }}
            >
              {especialidades.map((esp) => (
                <MenuItem key={esp.id} value={String(esp.id)}>
                  {esp.nombre}
                </MenuItem>
              ))}
            </TextField>

            {/* Ámbitos de trabajo */}
            <TextField
              select
              sx={{ minWidth: 200 }}
              SelectProps={{ multiple: true, renderValue: (sel) => `${sel.length} seleccionada(s)` }}
              size="small"
              label="Ámbitos de trabajo"
              value={filtersDraft.ambitoIds}
              onChange={(e) => {
                const val = e.target.value;
                setFiltersDraft((f) => ({
                  ...f,
                  ambitoIds: Array.isArray(val) ? val : [],
                }));
              }}
            >
              {ambitos.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {a.nombre}
                </MenuItem>
              ))}
            </TextField>

            {/* Lugares de trabajo */}
            <TextField
              select
              sx={{ minWidth: 200 }}
              SelectProps={{ multiple: true, renderValue: (sel) => `${sel.length} seleccionada(s)` }}
              size="small"
              label="Lugares de trabajo"
              value={filtersDraft.lugarIds}
              onChange={(e) => {
                const val = e.target.value;
                setFiltersDraft((f) => ({
                  ...f,
                  lugarIds: Array.isArray(val) ? val : [],
                }));
              }}
            >
              {lugares.map((l) => (
                <MenuItem key={l.id} value={String(l.id)}>
                  {l.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {/* Acciones filtros */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setPage(0);
              setFiltersApplied({ ...filtersDraft });
            }}
          >
            Buscar
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setFiltersDraft({ ...emptyFilters });
              setFiltersApplied({ ...emptyFilters });
              setPage(0);
            }}
          >
            Limpiar
          </Button>
        </Box>

        {/* Acciones generales */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenNuevo(true)}>
            Nuevo colegiado
          </Button>
          <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleExport}>
            Exportar
          </Button>
          <Button variant="outlined" onClick={() => setOpenINE(true)}>
            Estadística INE
          </Button>

          <Tooltip
            title={
              selectionCount ? `Editar ${selectionCount} seleccionados` : "Selecciona filas para editar en grupo"
            }
          >
            <span>
              <Button variant="outlined" onClick={() => setBulkOpen(true)} disabled={selectionCount === 0}>
                Edición masiva
              </Button>
            </span>
          </Tooltip>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Seleccionados: {selectionCount}
          </Typography>
        </Box>
      </Box>


      {/* === TABLA: scroll horizontal/vertical robusto === */}
      <Box
        sx={{
          width: '100%',
          // Altura fija: el propio DataGrid gestiona el scroll vertical
          height: { xs: '70vh', md: 'calc(100vh - 260px)' },
          // Importantísimo si su padre es flex: que pueda encoger
          minWidth: 0,
        }}
      >
        <DataGrid
          rows={displayedRows}
          getRowId={(row) => row.codigo}
          columns={columns}
          // ---- server stuff que ya tenías ----
          columnVisibilityModel={colVisibility}
          onColumnVisibilityModelChange={(m) => setColVisibility(m)}
          density="compact"
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m?.page ?? 0);
            setPageSize(m?.pageSize ?? pageSize);
          }}
          rowCount={filtersApplied.onlyConPendientes || filtersApplied.rolPendiente === "PENDIENTE"
            ? displayedRows.length
            : rowCount}
          pageSizeOptions={[10, 25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortChange}
          loading={loading}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          // ---- CLAVES PARA EL SCROLL ----
          autoHeight={false}                 // que no crezca en altura
          columnBuffer={Math.max(columns.length, 8)} // renderiza TODAS las columnas
          rowBuffer={8}                      // margen de filas extra (suave para UX)
          // (si tu versión soporta columnVirtualization, puedes añadir: columnVirtualization={false})

          sx={{
            // permite que el grid crezca más allá del ancho del contenedor
            '& .MuiDataGrid-virtualScrollerContent': {
              minWidth: `${Math.max(totalMinWidth, 900)}px`,
            },
            '& .MuiDataGrid-columnHeadersInner': {
              minWidth: `${Math.max(totalMinWidth, 900)}px`,
            },

            // estética mínima
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              fontSize: 13,
              py: 0.5,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            },
            '& .MuiDataGrid-columnHeaders': { fontSize: 12 },
            '& .MuiDataGrid-scrollbarFiller': { display: 'none' },
          }}
        />
      </Box>



      {/* Diálogo: nuevo colegiado */}
      <Dialog
        open={openNuevo}
        onClose={() => {
          setFormError("");
          setOpenNuevo(false);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 1100,
          },
        }}
      >
        <NuevoColegiadoDialog
          open={openNuevo}
          onClose={() => setOpenNuevo(false)}
          fetchPage={fetchPage}
          page={page}
          pageSize={pageSize}
          sortModel={sortModel}
          filtersApplied={filtersApplied}
          notify={notify}
        />
      </Dialog>

      {/* Edit individual */}
      <ColegiadoEditDialog
        open={editOpen}
        codigo={editCodigo}
        onClose={closeEdit}
        onSaved={() => {
          fetchPage(page, pageSize, sortModel, filtersApplied);
        }}
        showAltaBaja
      />

      {/* NUEVO: Edit masivo */}
      <BulkEditDialog
        open={bulkOpen}
        selectedCodes={Array.from(selectedCodes)}
        onClose={() => setBulkOpen(false)}
        onApplied={() => {
          setBulkOpen(false);
          fetchPage(page, pageSize, sortModel, filtersApplied);
        }}
      />
      <EstadisticasIne open={openINE} onClose={() => setOpenINE(false)} />
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={toMessage(snack.msg)}
      />
    </Paper>
  );
}
